import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthorSignature } from "../components/AuthorSignature";
import { TableShareButton } from "../components/TableShare";
import { ArrowLeft, ArrowRight, Linkedin, MessageCircle, Link2, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { localApi } from "../lib/localApi";
import { useSeo } from "../lib/seo";
import { TopBar, Footer } from "./landing/LandingSections";
import { NewsletterForm } from "../components/NewsletterForm";
import { RelatedGuides } from "../components/RelatedGuides";
import { CasaneoInBrief } from "../components/CasaneoInBrief";
import { SupportChatBubble } from "../components/SupportChatBubble";

/* Rendu minimal et sûr d'un markdown simple : titres (#, ##, ###), listes (-), paragraphes, **gras**, [liens](url). */
function inline(text) {
  const parts = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g;
  let last = 0, m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) parts.push(<strong key={m.index} className="text-white">{m[1]}</strong>);
    else parts.push(m[3].startsWith("/") ? <Link key={m.index} to={m[3]} className="text-brass underline">{m[2]}</Link> : <a key={m.index} href={m[3]} target="_blank" rel="noopener noreferrer" className="text-brass underline">{m[2]}</a>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function GuideTable({ head, rows, share }) {
  const ref = useRef(null);
  return (
    <div>
      <div ref={ref} className="overflow-x-auto rounded-xl border border-white/10" data-testid="guide-table">
        <table className="w-full text-sm text-left">
          <thead className="bg-white/[0.05] text-white"><tr>{head.map((c, j) => <th key={j} className="px-3 py-2 font-medium">{inline(c)}</th>)}</tr></thead>
          <tbody>{rows.map((r, k) => <tr key={k} className="border-t border-white/10 text-slate-300">{r.map((c, j) => <td key={j} className="px-3 py-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
        </table>
      </div>
      {share && <div className="mt-2 flex justify-end"><TableShareButton tableRef={ref} title={share.title} pageUrl={share.pageUrl} testId="guide-table-share" /></div>}
    </div>
  );
}

export function Markdown({ text, share }) {
  const blocks = [];
  let list = null, olist = null, table = null;
  const flush = () => {
    if (list) { blocks.push(<ul key={blocks.length} className="list-disc pl-6 space-y-1.5 text-slate-300">{list}</ul>); list = null; }
    if (olist) { blocks.push(<ol key={blocks.length} className="list-decimal pl-6 space-y-1.5 text-slate-300">{olist}</ol>); olist = null; }
    if (table) {
      const [head, ...rows] = table;
      blocks.push(<GuideTable key={blocks.length} head={head} rows={rows} share={share} />);
      table = null;
    }
  };
  (text || "").split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (line.startsWith("|")) {
      const cells = line.slice(1, line.endsWith("|") ? -1 : undefined).split("|").map((c) => c.trim());
      if (cells.every((c) => /^:?-{3,}:?$/.test(c))) return;
      (table ||= []).push(cells);
      return;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) { (list ||= []).push(<li key={i}>{inline(line.slice(2))}</li>); return; }
    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) { (olist ||= []).push(<li key={i}>{inline(ol[2])}</li>); return; }
    flush();
    const img = line.match(/^!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)$/);
    if (img) { blocks.push(<figure key={i} className="my-6"><img src={img[2]} alt={img[1]} loading="lazy" className="w-full rounded-2xl border border-white/10" data-testid="guide-image" />{img[1] && <figcaption className="mt-2 text-xs text-slate-400">{img[1]}</figcaption>}</figure>); return; }
    if (line.startsWith("### ")) blocks.push(<h3 key={i} className="text-lg font-semibold text-white mt-6">{inline(line.slice(4))}</h3>);
    else if (line.startsWith("## ")) blocks.push(<h2 key={i} className="font-serif text-2xl text-white mt-10">{inline(line.slice(3))}</h2>);
    else if (line.startsWith("# ")) blocks.push(<h2 key={i} className="font-serif text-3xl text-white mt-10">{inline(line.slice(2))}</h2>);
    else if (line.startsWith("> ")) blocks.push(<blockquote key={i} className="border-l-2 border-brass/60 bg-brass/[0.06] rounded-r-xl px-4 py-3 text-sm text-slate-200 leading-relaxed" data-testid="guide-callout">{inline(line.slice(2))}</blockquote>);
    else blocks.push(<p key={i} className="text-slate-300 leading-relaxed">{inline(line)}</p>);
  });
  flush();
  return <div className="space-y-4">{blocks}</div>;
}

export function FaqJsonLd({ faq, id = "faq-jsonld" }) {
  useEffect(() => {
    const valid = (faq || []).filter((f) => f.q && f.a);
    let el = document.getElementById(id);
    if (!valid.length) { el?.remove(); return; }
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
    el.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: valid.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    return () => document.getElementById(id)?.remove();
  }, [faq, id]);
  return null;
}

/* Partage LinkedIn / WhatsApp / copie : l'URL partagée est la page « robots » qui porte les balises OG + image générée. */
function ShareBar({ slug, title, compact = false }) {
  const [copied, setCopied] = useState(false);
  const origin = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = `${origin}/api/public/share/guide/${slug}`;
  const pageUrl = `${origin}/guide/${slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(pageUrl); setCopied(true); toast.success("Lien copié"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copie impossible — sélectionnez l'adresse dans la barre du navigateur"); }
  };
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title, url: pageUrl }); } catch { /* annulé */ } }
    else copy();
  };
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10 hover:text-white transition-colors";
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? "mt-6" : "mt-12 pt-6 border-t border-white/10"}`} data-testid={compact ? "seo-guide-share-top" : "seo-guide-share-bottom"}>
      {!compact && <span className="text-xs uppercase tracking-[0.2em] text-brass mr-2">Partager ce guide</span>}
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className={btn} data-testid={`share-linkedin${compact ? "-top" : ""}`}><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${title} — ${shareUrl}`)}`} target="_blank" rel="noreferrer" className={btn} data-testid={`share-whatsapp${compact ? "-top" : ""}`}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
      <button type="button" onClick={copy} className={btn} data-testid={`share-copy${compact ? "-top" : ""}`}>{copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Link2 className="h-3.5 w-3.5" />} {copied ? "Copié" : "Copier le lien"}</button>
      {!compact && <button type="button" onClick={native} className={btn} data-testid="share-native"><Share2 className="h-3.5 w-3.5" /> Autre…</button>}
    </div>
  );
}

export default function SeoGuide() {
  const { slug } = useParams();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["seo-page", slug], queryFn: async () => (await localApi.get(`/public/seo-pages/${slug}`)).data, retry: (n, e) => e?.response?.status !== 404 && n < 3, retryDelay: (n) => 1500 * (n + 1) });
  const { data: others } = useQuery({ queryKey: ["seo-pages"], queryFn: async () => (await localApi.get("/public/seo-pages")).data.items, staleTime: 600_000 });
  const notFound = isError && error?.response?.status === 404;
  useSeo({ title: data ? `${data.title} — Casanéo` : notFound ? "Page introuvable — Casanéo" : "Guide — Casanéo", description: data?.description || "", path: `/guide/${slug}`, image: `/api/public/og/guide/${slug}.png`, noindex: notFound });
  return (
    <div className="landing typo-a min-h-screen bg-[#071e42] text-slate-100" data-testid="seo-guide-page">
      <TopBar />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="seo-guide-back"><ArrowLeft className="h-3.5 w-3.5" /> Casanéo</Link>
        {notFound ? (
          <div className="mt-8" data-testid="seo-guide-missing"><h1 className="font-serif text-4xl text-white">Page introuvable</h1><p className="mt-3 text-slate-400">Ce guide n'existe pas ou n'est plus publié.</p></div>
        ) : isLoading || !data ? (
          <div className="mt-8" data-testid="seo-guide-loading">
            <p className="text-slate-400">Chargement du guide…</p>
            {isError && <button type="button" onClick={() => refetch()} className="mt-3 text-xs underline text-brass" data-testid="seo-guide-retry">Le chargement a échoué — réessayer</button>}
          </div>
        ) : (
          <article>
            <p className="text-xs uppercase tracking-[0.2em] text-brass mt-6">Guide</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mt-2" data-testid="seo-guide-title">{data.title}</h1>
            {data.description && <p className="mt-4 text-base md:text-lg text-slate-300">{data.description}</p>}
            <ShareBar slug={slug} title={data.title} compact />
            {data.cover && <img src={data.cover} alt={data.cover_alt || data.title} className="mt-8 w-full rounded-2xl border border-white/10 object-cover max-h-[420px]" data-testid="seo-guide-cover" />}
            <div className="mt-10"><Markdown text={data.body} share={{ title: data.title, pageUrl: `${process.env.REACT_APP_BACKEND_URL}/guide/${slug}` }} /></div>
            <CasaneoInBrief />
            <AuthorSignature guide={data} />
            <ShareBar slug={slug} title={data.title} />
            <div className="mt-12"><NewsletterForm source={`guide:${slug}`} compact /></div>
            {!!(data.faq || []).length && (
              <section className="mt-12" data-testid="seo-guide-faq">
                <h2 className="font-serif text-2xl text-white">Questions fréquentes</h2>
                <dl className="mt-4 divide-y divide-white/10">{data.faq.map((f, i) => <div key={i} className="py-4"><dt className="font-semibold text-white">{f.q}</dt><dd className="mt-1.5 text-slate-300">{f.a}</dd></div>)}</dl>
                <FaqJsonLd faq={data.faq} />
              </section>
            )}
            <div className="mt-12 rounded-2xl border border-brass/40 bg-brass/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-white">Essayez Casanéo gratuitement pendant 14 jours, sans carte bancaire.</p>
              <Link to="/casaneo-register" className="inline-flex items-center gap-2 rounded-full bg-brass px-5 py-2.5 text-sm font-medium text-[#0b1220]" data-testid="seo-guide-cta">Créer mon compte <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <RelatedGuides current={{ ...data, slug }} items={others} />
          </article>
        )}
      </main>
      <Footer />
      <SupportChatBubble />
    </div>
  );
}
